// Le site du club rendu pour le visiteur (D-140, maquette s6) : à l'adresse du club (<club>.arceau.app, à la racine ; en
// local, /c/<adresse>/…) quand il est publié, que l'option « Site personnalisable » est active et que la page du club est
// en ligne. Sinon, la page gratuite actuelle, sans changement (src/ui/public.ts). Les pages se suivent sans recharger
// (le menu, les pages d'équipe, les actualités) ; « Venez essayer », « Devenir partenaire », l'inscription à un
// événement et « ☆ Suivre » ouvrent les mêmes formulaires que la page gratuite (aucune donnée nouvelle).
import { clubPublicPhoto, trackPartners } from '../lib/api';
import { followState } from '../lib/follow-api';
import { clubLegal } from '../lib/minors-legal';
import { liveDataBySlug, mediaUrl, type PublicSite, type SiteData } from '../lib/site-api';
import { supabase } from '../lib/supabase';
import { resolvePath, seoDesc, seoTeamDesc, seoTitle, type SiteRoute } from '../domain/site';
import type { FollowGroup, FollowState } from '../domain/follow';
import { html, mount, openSheet } from './dom';
import { applyBrand } from './theme';
import { openEventSignup, openPartnership, openTrial } from './public';
import { openPublicFollow, type PublicFollow } from './suivre';
import { downloadIcs, hydrateMedia, renderSite, siteGroups, wireSite, type SiteView } from './site-render';

export interface SiteBase {
  /** Sous le sous-domaine du club : '' ; sinon « /c/<adresse> ». */
  prefix: string;
  /** L'espace membres. */
  members: string;
}

/** Le site publié, à ce chemin (« », « le-club », « equipes/u13-filles », « actualites/<id> »). */
export async function renderSitePublic(root: HTMLElement, slug: string, site: PublicSite, path: string, base: SiteBase): Promise<void> {
  const [data, legal] = await Promise.all([liveDataBySlug(slug).catch(() => null), clubLegal({ slug }).catch(() => null)]);
  const pub = data?.pub ?? null;
  const club = pub?.club ?? { name: legal?.name ?? '', short_name: legal?.short_name ?? null, logo: legal?.logo ?? null, color: legal?.color ?? null,
    tagline: null, email: legal?.contact_email ?? null, address: null, trial: false, timezone: 'Europe/Paris' };
  applyBrand({ id: `public:${slug}`, name: club.name, short_name: club.short_name, logo_url: club.logo, primary_color: club.color }, false);
  const link = (p: string) => `${base.prefix}/${p}`.replace(/\/+$/, '') || '/';
  const v: SiteView = {
    doc: site.doc, club, data, news: site.news, shop: site.shop, director: site.director, legal, color2: site.color2, mode: 'public', link,
    membersHref: base.members, dataHref: `${base.prefix}/donnees`, partnersHref: `${base.prefix}/partenaires`,
  };
  const groups = siteGroups(v);
  let follow: FollowState | null = null;
  const loadFollow = async () => { follow = (await supabase?.auth.getSession())?.data.session ? await followState({ slug }).catch(() => null) : null; };
  void loadFollow();

  const draw = (p: string) => {
    const route: SiteRoute | null = resolvePath(site.doc, groups, p);
    mount(root, renderSite(v, route));
    document.title = titleOf(v, route);
    setMeta('description', descOf(v, route, data));
    hydrateMedia(root, id => mediaUrl(id, { slug }), id => clubPublicPhoto(slug, id).then(b => (b ? `data:image/jpeg;base64,${b}` : null)).catch(() => null));
    window.scrollTo(0, 0);
    // Visibilité des partenaires (D-033) : un affichage par page vue qui les montre, un clic par lien suivi.
    const shown = [...root.querySelectorAll<HTMLElement>('[data-pclick]')].map(a => a.dataset.pclick!);
    if (shown.length) trackPartners([...new Set(shown)], 'public');
  };
  const groupsF = (): FollowGroup[] => groups.map(t => follow?.groups.find(g => g.team_id === t.id)
    ?? { team_id: t.id, name: t.name, category: t.category ?? null, gender: t.gender, squads: t.squads, follow: null, auto: null, followers: 0 });
  const pf: PublicFollow = { slug, tz: club.timezone || 'Europe/Paris', upcoming: pub?.upcoming ?? [], refresh: loadFollow };
  const categories = [...new Set((pub?.teams ?? []).map(t => t.category).filter((c): c is string => !!c))];

  wireSite(root, {
    trial: () => { if (club.trial) openTrial(slug, club.name, categories); else if (club.email) location.href = `mailto:${club.email}`; },
    partner: () => openPartnership(slug, club.name),
    event: (el) => { const ev = data?.agenda.find(e => e.id === el.dataset.event); if (ev) openEventSignup(slug, ev, () => undefined); },
    follow: (el) => {
      const list = groupsF();
      const g = list.find(x => x.team_id === el.dataset.group) ?? (list.length === 1 ? list[0] : null);
      if (g) { openPublicFollow(pf, g); return; }
      // « ☆ Suivre un groupe » (pied de page) : le groupe d'abord.
      openSheet('Suivre un groupe', html`<p class="muted" style="margin:0">Résultats et changements de dernière minute, sur votre téléphone ou par e-mail.</p>
        <div class="se-chips">${list.map(x => html`<button type="button" class="ac-chip" data-fg="${x.team_id}">☆ ${x.name}</button>`)}</div>`, (sheet, close) => {
        for (const b of sheet.querySelectorAll<HTMLButtonElement>('[data-fg]')) b.addEventListener('click', () => { close(); openPublicFollow(pf, list.find(x => x.team_id === b.dataset.fg)!); });
      });
    },
    ics: (el) => downloadIcs(el.dataset.title ?? 'Match', el.dataset.at ?? new Date().toISOString(), el.dataset.where ?? ''),
    doc: (el) => void openDoc(slug, el.dataset.doc ?? ''),
  });
  // Les pages du site se suivent sans recharger ; l'espace membres, les mentions et les liens extérieurs, normalement.
  root.addEventListener('click', (e) => {
    const pc = (e.target as HTMLElement).closest<HTMLElement>('[data-pclick]');
    if (pc) trackPartners([pc.dataset.pclick!], 'public', true);
    const a = (e.target as HTMLElement).closest<HTMLAnchorElement>('a[href]');
    if (!a || e.defaultPrevented || a.target || e.metaKey || e.ctrlKey || e.button !== 0) return;
    const href = a.getAttribute('href')!;
    if (!href.startsWith('/') || href.startsWith('//')) return;
    const rest = base.prefix ? (href === base.prefix || href.startsWith(`${base.prefix}/`) ? href.slice(base.prefix.length) : null) : href;
    if (rest === null || /^\/(app|donnees|partenaires)(\/|$)/.test(rest)) return;
    e.preventDefault();
    history.pushState({ site: true }, '', href);
    draw(rest.replace(/^\/+/, ''));
  });
  window.addEventListener('popstate', () => {
    const p = location.pathname;
    const rest = base.prefix ? p.slice(base.prefix.length) : p;
    draw(rest.replace(/^\/+/, ''));
  });
  draw(path.replace(/^\/+|\/+$/g, ''));
}

function titleOf(v: SiteView, route: SiteRoute | null): string {
  if (!route) return `Page introuvable · ${v.club.name}`;
  if (route.kind === 'page') return route.page.seo.title || seoTitle(route.page.title, v.club.name, v.club.tagline, route.page.kind === 'home');
  if (route.kind === 'team') return v.doc.teams.seo[route.group.id]?.title || seoTitle(route.group.name, v.club.name);
  if (route.kind === 'news') return seoTitle('Actualités', v.club.name);
  return seoTitle(v.news.find(n => n.id === route.id)?.title ?? 'Actualité', v.club.name);
}
function descOf(v: SiteView, route: SiteRoute | null, data: SiteData | null): string {
  const fallback = v.club.tagline ?? v.club.name;
  if (route?.kind === 'page') return route.page.seo.desc || seoDesc(route.page, fallback);
  if (route?.kind === 'team') {
    const g = siteGroups(v).find(t => t.id === route.group.id);
    const next = (data?.pub.upcoming ?? []).find(m => m.is_home && m.group_id === route.group.id);
    return v.doc.teams.seo[route.group.id]?.desc || (g ? seoTeamDesc(g.slots, next ? { versus: `${next.team} – ${next.opponent}`,
      date: new Intl.DateTimeFormat('fr-FR', { timeZone: v.club.timezone, day: 'numeric', month: 'long' }).format(new Date(next.starts_at)) } : null) : fallback);
  }
  return fallback;
}
function setMeta(name: string, content: string): void {
  let m = document.querySelector<HTMLMetaElement>(`meta[name="${name}"]`);
  if (!m) { m = document.createElement('meta'); m.name = name; document.head.append(m); }
  m.content = content;
}
/** Un document PDF du site : ouvert dans un nouvel onglet. */
async function openDoc(slug: string, id: string): Promise<void> {
  if (!id) return;
  const url = await mediaUrl(id, { slug });
  if (!url) return;
  const blob = await (await fetch(url)).blob();
  window.open(URL.createObjectURL(blob), '_blank', 'noopener');
}
